import { apiClient } from '@/auth';

export type PatientListRow = {
  patient_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string | null;
  is_registered: boolean;
  registered_by_user_id?: number;
  tracking_start_date?: string | null;
  last_meal_date?: string | null;
  next_report_date?: string | null;
  last_completed_report_date?: string | null;
  has_active_subscription?: boolean;
  has_completed_subscription?: boolean;
  has_expired_subscription?: boolean;
  has_upcoming_subscription?: boolean;
  most_recent_subscription_start_date?: string | null;
  has_draft_report?: boolean;
  clinics?: Array<{ id: number; name: string }>;
  invitation_status?: string | null;
  invitation_id?: number | null;
  /** Per `PatientHospitalAttributes` (swagger:18795). Carries the
   *  clinic-defined external chart id and the bag of custom-field values
   *  (keyed by the `field` strings in each clinic's
   *  `hospital_patient_custom_fields_json` flag). */
  hospital_attributes?: {
    hospital_id?: number;
    patient_external_id?: string | null;
    patient_custom_fields?: Record<string, string | null | undefined>;
  };
};

export type PatientsTablePage = {
  rows: PatientListRow[];
  has_more: boolean;
  total_rows?: number;
  next?: string;
};

export type PatientsTableParams = {
  date?: string;
  filter?: string;
  limit?: number;
  offset?: string;
  order?: string;
};

/**
 * Structured filter that the server understands when JSON-serialized into
 * the `filter` query param. Mirrors v1's `filterObj` (clinician/src/pages/
 * patientlist.tsx:554-616). Each known field is paired with an operator —
 * either a comparison (`{contains, eq, ne}`) or `array_contains_any` for
 * multi-select arrays — and the server applies them all conjunctively.
 *
 * Pass plain `{}` for "all patients". Omit the `filter` param entirely (or
 * pass undefined) to get the same result.
 */
export type PatientsTableFilter = {
  nameEmail?: { contains: string };
  clinic_ids?: { array_contains_any: number[] };
  has_active_subscription?: boolean;
  has_completed_subscription?: boolean;
  has_upcoming_subscription?: boolean;
  has_expired_subscription?: boolean;
  has_draft_report?: boolean;
  registered_by_user_id?: { eq?: number; ne?: number };
  /** Date comparison filters — server accepts ISO YYYY-MM-DD strings with
   *  `le` (≤) / `ge` (≥) operators. Used by v1's "hasn't logged 1mo" and
   *  "report soon" flag filters (patientlist.tsx:557-566). */
  last_meal_date?: { le?: string; ge?: string };
  next_report_date?: { le?: string; ge?: string };
};

/**
 * Free-form name + comment pair used by both `motivations` and
 * `diet_restrictions`. The `name` is a server enum (`mediterranean diet`,
 * `keto`, `other`...) and `comment` is a free-text note used when the user
 * picked "other".
 */
export type PatientNamedTag = {
  name: string;
  comment?: string | null;
};

export type Patient = PatientListRow & {
  birthday?: string;
  sex?: string;
  patient_type?: string;
  /**
   * Resolved patient flag bag — clinic defaults overlaid with any
   * per-patient `flags_raw` overrides. Used to read settings like
   * `patient_glucose_units_mmol_l`, the various `patient_app_*` toggles, etc.
   */
  flags?: Record<string, unknown>;
  /**
   * Just the patient's OVERRIDES (what's been explicitly set per-patient,
   * excluding clinic defaults). EditPatientDialog uses this to seed its
   * flags form so editing surfaces the existing overrides rather than
   * defaulting back to clinic values.
   */
  flags_raw?: Record<string, string | boolean | number | null>;
  hospital_id?: number;
  /** Patient's selected motivations from the onboarding flow. */
  motivations?: PatientNamedTag[];
  /** Patient's dietary restrictions from the onboarding flow. */
  diet_restrictions?: PatientNamedTag[];
};

/**
 * @deprecated `patient_type` is no longer surfaced in V2 forms — the team
 * marked it as on its way out. The enum stays exported (and the type below
 * still references it) so any code path that round-trips an existing value
 * through PATCH doesn't lose data, but new patients should not have it set.
 *
 * Tracked in FOLLOW_UPS.md under "Deprecated v1 surfaces".
 */
export const PATIENT_TYPES = [
  'wellness',
  'obesity',
  'diabetes',
  'metabolic syndrome',
  'weight loss',
  'migraine',
  'gastroesophageal reflux',
  'gi discomfort',
  'vertigo',
  'sleep apnea',
  'menopause',
  'research',
  'collaboration',
  'rxlife',
  'dexcom',
] as const;

export const SUBSCRIPTION_NAMES = [
  'none',
  'three-day',
  'five-day',
  'seven-day',
  'ten-day',
  '14-day',
  '21-day',
  '28-day',
  'one-month',
  'cgm14',
  'custom',
] as const;

/**
 * Body for `POST /patients/from_clinician` (MNT swagger:14651). Mirrors
 * v1's `CreatePatientFromClinicianRequest`. Only `first_name/last_name/
 * email/clinic_id` are required server-side; the rest are optional and
 * conditionally relevant based on subscription cadence + clinic flags.
 *
 * - `hospital_patient_external_id` + `hospital_patient_custom_fields`:
 *   only sent when the hospital has those features enabled (driven by
 *   the `hospital_patient_has_external_id` flag and the
 *   `hospital_patient_custom_fields_json` schema).
 * - `subscription_type` + `subscription_expiration_date` + `first_report_date`:
 *   only relevant when an initial subscription is being created (cadence
 *   != none). For recurring (weekly / bi-weekly / 4-weeks / monthly), the
 *   expiration is the end of the recurring sequence; for single, the
 *   first_report_date is the report delivery date.
 */
export type CreatePatientInput = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string | null;
  clinic_id: number;
  hospital_patient_external_id?: string | null;
  hospital_patient_custom_fields?: Record<string, string> | null;
  tracking_start_date?: string;
  patient_type?: (typeof PATIENT_TYPES)[number];
  subscription_name?: (typeof SUBSCRIPTION_NAMES)[number];
  subscription_type?: 'single' | 'weekly' | 'monthly' | 'bi-weekly' | '4-weeks' | 'custom';
  subscription_expiration_date?: string;
  first_report_date?: string;
  locale?: string;
  /**
   * Per-patient flag overrides. Server merges these on top of the clinic
   * defaults — any key absent here keeps the clinic value. Maps to v1's
   * `flags_raw`.
   */
  flags_raw?: Record<string, string | boolean | number | null>;
};

export const PATIENT_SEXES = ['female', 'male', 'intersex', 'mtf_female', 'ftm_male'] as const;
export type PatientSex = (typeof PATIENT_SEXES)[number];

/**
 * Body for PATCH /patients/{id} — every field is optional. Per swagger:14743,
 * fields left out keep their current server value; explicit null also keeps
 * the value (the endpoint won't write null over a real one).
 */
export type PatchPatientInput = {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string | null;
  sex?: PatientSex | null;
  birthday?: string | null;
  patient_type?: (typeof PATIENT_TYPES)[number] | null;
  locale?: string | null;
  meal_time_breakfast?: string | null;
  meal_time_lunch?: string | null;
  meal_time_dinner?: string | null;
  /** Per-patient flag overrides. Same semantics as
   *  `CreatePatientInput.flags_raw` — keys absent here keep clinic
   *  defaults; explicit values override. */
  flags_raw?: Record<string, string | boolean | number | null>;
};

/** Per MNT swagger:14969 (`PatientMedicationResponse`). */
export type PatientMedication = {
  id: number;
  medication_date: string;
  medication_time: string;
  name: string;
  quantity: number;
  unit: string;
  note?: string | null;
  side_effects?: string | null;
};

export const patientsApi = {
  /**
   * Patient table for a clinician. Endpoint:
   *   GET /hospitals/{hospital_id}/clinicians/{clinician_id}/patients/table
   * Verified against MNT swagger:5601 + v1 PatientTable.tsx:261.
   *
   * Filter handling matches v1: when a structured `filter` is provided, it's
   * JSON-serialized into the `filter` query param. Server interprets the
   * encoded object's per-field operators (`contains`, `array_contains_any`,
   * `eq`, etc.) and ANDs them.
   */
  async listForClinician(
    clinicianId: number,
    params?: Omit<PatientsTableParams, 'filter'> & { filter?: PatientsTableFilter | string },
  ): Promise<PatientsTablePage> {
    const { filter, ...rest } = params ?? {};
    const filterParam =
      typeof filter === 'string'
        ? filter
        : filter && Object.keys(filter).length > 0
          ? JSON.stringify(filter)
          : undefined;
    // Clinician-scoped, multi-hospital roster (FOOD-841): spans every hospital
    // the clinician belongs to, not just their home one. Replaces the old
    // `/hospitals/{h}/clinicians/{c}/patients/table` — same swap FOOD-837 made
    // for clinics. Requires MNT #3978 deployed.
    const res = await apiClient.get(
      `/clinicians/${clinicianId}/patients/table`,
      { params: { ...rest, ...(filterParam ? { filter: filterParam } : {}) } },
    );
    const data = res.data;
    return {
      rows: data.rows ?? [],
      has_more: Boolean(data.has_more),
      total_rows: data.total_rows,
      next: data.next,
    };
  },

  /**
   * Patient medications log. Per MNT swagger:10859 (`get_patient_medications`).
   * Returns one row per logged medication event — not a current-meds list.
   * Caller typically dedupes by `name` and keeps the latest per name to
   * render a "current medications" summary in the patient rail.
   */
  async medications(
    patientId: number,
    params?: { from?: string; to?: string },
  ): Promise<PatientMedication[]> {
    try {
      const res = await apiClient.get<PatientMedication[]>(
        `/patients/${patientId}/medications`,
        { params: { date_since: params?.from, date_until: params?.to } },
      );
      return Array.isArray(res.data) ? res.data : [];
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 403 || status === 404) return [];
      throw err;
    }
  },

  async get(patientId: number): Promise<Patient> {
    const res = await apiClient.get<Patient & { id?: number }>(`/patients/${patientId}`);
    const data = res.data;
    // `GET /patients/{id}` returns `PatientResponse` (swagger:14223) with
    // an `id` field, not `patient_id`. The patient-LIST row schema is the
    // only place `patient_id` lives natively. The V2 `Patient` type
    // unifies them as `patient_id`, so we copy `id` over at the edge to
    // keep the rest of the app honest — without this, any code that
    // reads `patient.patient_id` (e.g. the patient-list ID-match hoist)
    // silently fails because the field is undefined at runtime.
    if (data && data.patient_id === undefined && typeof data.id === 'number') {
      return { ...data, patient_id: data.id };
    }
    return data;
  },

  /**
   * POST /patients/from_clinician — creates a new patient on behalf of the
   * current clinician. `clinic_id` is required (provided by the form via the
   * clinician's clinics list).
   */
  async create(input: CreatePatientInput): Promise<Patient> {
    const res = await apiClient.post(`/patients/from_clinician`, input);
    return res.data;
  },

  /**
   * PATCH /patients/{id} — partial update. Only fields present in the body
   * are touched. Verified swagger.yaml:1236.
   */
  async update(patientId: number, input: PatchPatientInput): Promise<Patient> {
    const res = await apiClient.patch(`/patients/${patientId}`, input);
    return res.data;
  },

  /**
   * GET /patients/{id}/clinics — list of clinics the patient belongs to.
   * Used by the logging-period form to compute the intersection with the
   * clinician's own clinics (matches v1's `sharedClinics`). Verified MNT
   * swagger:1258.
   *
   * Imported lazily as `ClinicResponse` from `@/api/hospital` to avoid a
   * circular dep; we just type the response inline here.
   */
  async clinics(patientId: number): Promise<Array<{ id: number; name: string; hospital_id?: number; flags?: Record<string, unknown> }>> {
    const res = await apiClient.get(`/patients/${patientId}/clinics`);
    return Array.isArray(res.data) ? res.data : [];
  },
};
